import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { Sidebar } from './Sidebar';
import { BrandIdentity } from '@/components/branding/BrandIdentity';
import { IconMenu } from './sidebar-icons';
import styles from './AppShell.module.scss';

const FULLBLEED_PREFIXES = ['/chat'];

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { loading } = useActiveWorkspace();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isFullbleed = FULLBLEED_PREFIXES.some((prefix) =>
    location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMobileNavOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  if (loading) {
    return (
      <div className={styles.shellLoading}>
        <span>Carregando workspace...</span>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <header className={styles.mobileBar}>
        <button
          type="button"
          className={styles.mobileMenuButton}
          onClick={() => setMobileNavOpen(true)}
          aria-label="Abrir navegação"
          aria-expanded={mobileNavOpen}
          aria-controls="app-navigation"
        >
          <IconMenu />
        </button>
        <BrandIdentity className={styles.mobileBrand} />
      </header>
      {mobileNavOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={() => setMobileNavOpen(false)}
          aria-label="Fechar navegação"
        />
      )}
      <Sidebar mobileOpen={mobileNavOpen} onRequestClose={() => setMobileNavOpen(false)} />
      <main className={`${styles.main} ${isFullbleed ? styles.mainFullbleed : ''}`}>
        {children}
      </main>
    </div>
  );
}
